import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormState, MealPlanData } from "types/mealPlans";

import MealPlanForm from "./components/MealPlanForm";
import MealPlanPreview from "./components/MealPlanPreview";
import MealPlanResults from "./components/MealPlanResults";

import { useApiKey } from "context/ApiKeyContext";

import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import Container from "components/atoms/Container";
import NavHeader from "components/organisms/NavHeader";

import MealPlanApiClient from "utils/mealPlanApiClient";

const MealPlans = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { apiKey } = useApiKey();
  const [apiClient, setApiClient] = useState<MealPlanApiClient | null>(null);

  // State for custom token toast
  const [tokenToast, setTokenToast] = useState<{
    visible: boolean;
    message: string;
  }>({ visible: false, message: "" });

  // State for meal plan data
  const [previewData, setPreviewData] = useState<MealPlanData | null>(null);
  const [fullMealPlan, setFullMealPlan] = useState<MealPlanData | null>(null);
  const [activeView, setActiveView] = useState<"preview" | "full" | null>(null);
  const [currentFormState, setCurrentFormState] = useState<FormState | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Initialize API client when apiKey is available
  useEffect(() => {
    if (apiKey) {
      try {
        const client = new MealPlanApiClient(apiKey);
        setApiClient(client);
        setError(null);
      } catch (err) {
        setError("Failed to initialize API client. Please try again.");
      }
    } else {
      setApiClient(null);
    }
  }, [apiKey]);

  // Method to convert form data to constraints format
  const formToConstraints = (formState: FormState) => {
    // Create constraints object - only include user input data, no calculated macros
    const constraints = {
      constraints: {
        age: formState.age,
        gender: formState.sex || "other",
        goal: formState.additionalInfo || "balanced",
        dietaryRestrictions: formState.dietaryRestrictions || [],
        allergies: formState.allergies || [],
        weight: formState.weight,
        height: formState.height,
        activityLevel: formState.activityLevel,
        days: formState.days.toString(),
      },
      // No macros - let the AI extract from goal text if provided
    };

    return constraints;
  };

  // Method to prepare initial request for meal plan generation
  const prepareInitialRequest = (formState: FormState): MealPlanData => {
    return {
      constraints: formToConstraints(formState),
      mealPlanDays: [], // Empty initially, will be filled by API
    };
  };

  // Method to display token usage (custom implementation)
  const showTokenUsageToast = (tokenUsage?: number, type = "meal plan") => {
    if (tokenUsage !== undefined) {
      // Clear any existing toasts from the library
      toast.dismiss();

      // Set our custom toast
      setTokenToast({
        visible: true,
        message: `${
          type.charAt(0).toUpperCase() + type.slice(1)
        } generated using ${tokenUsage.toLocaleString()} tokens`,
      });

      // Auto-hide after 10 seconds
      setTimeout(() => {
        setTokenToast((prev) => ({ ...prev, visible: false }));
      }, 10000);
    }
  };

  const handleGenerateMealPlan = async (formState: FormState) => {
    setIsLoading(true);
    setError(null);

    if (!apiClient) {
      setError("API client not initialized. Please check your API key.");
      setIsLoading(false);
      return;
    }

    try {
      let requestData: MealPlanData;

      // Check if we have a preview
      if (previewData) {
        // For full plan, send the entire preview data
        requestData = previewData;
      } else {
        // First-time generation without preview
        requestData = prepareInitialRequest(formState);
      }

      // Make API call
      const response = await apiClient.generateMealPlan(
        JSON.stringify(requestData)
      );

      // Set meal plan data from response
      setFullMealPlan(response.data);
      setActiveView("full");

      // Show token usage toast if available - with a slight delay to ensure no collisions
      setTimeout(() => {
        showTokenUsageToast(response.tokenUsage, "full meal plan");
      }, 100);
    } catch (err: any) {
      setError(
        err.message || "Failed to generate meal plan. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePreview = async (formState: FormState) => {
    setIsLoading(true);
    setError(null);

    // Store the current form state
    setCurrentFormState(formState);

    if (!apiClient) {
      setError("API client not initialized. Please check your API key.");
      setIsLoading(false);
      return;
    }

    try {
      let requestData: MealPlanData;

      // Check if we already have preview data with rejections
      if (previewData && hasRejectedRecipes()) {
        // Maintain the rejected recipes but update constraints
        requestData = {
          ...previewData,
          constraints: formToConstraints(formState),
        };
      } else {
        // First-time preview
        requestData = prepareInitialRequest(formState);
      }

      // Make API call
      const response = await apiClient.generateMealPlanPreview(
        JSON.stringify(requestData)
      );

      // Set preview data from response
      setPreviewData(response.data);
      setActiveView("preview");

      // Show token usage toast if available
      showTokenUsageToast(response.tokenUsage, "meal plan preview");
    } catch (err: any) {
      setError(err.message || "Failed to generate preview. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle imported meal plan data
  const handleImportMealPlan = (importedData: MealPlanData) => {
    try {
      // Check if it has detailed data (like a full meal plan)
      const hasDetailedRecipes = importedData.mealPlanDays.some(
        (day) =>
          day.breakfast.recipes.some((recipe) => recipe.preparation) ||
          day.lunch.recipes.some((recipe) => recipe.preparation) ||
          day.dinner.recipes.some((recipe) => recipe.preparation) ||
          day.snack.recipes.some((recipe) => recipe.preparation)
      );

      if (hasDetailedRecipes) {
        // Treat as a full meal plan
        setFullMealPlan(importedData);
        setActiveView("full");
      } else {
        // Treat as a preview
        setPreviewData(importedData);
        setActiveView("preview");
      }

      // Show success message
      alert("Meal plan imported successfully!");
    } catch (err) {
      setError("The imported file doesn't contain valid meal plan data.");
    }
  };

  const handleRejectRecipe = (
    dayIndex: number,
    mealType: "breakfast" | "lunch" | "dinner" | "snack",
    recipeIndex: number
  ) => {
    if (!previewData) return;

    const updatedPreviewData = { ...previewData };

    // Create rejected flag if it doesn't exist
    if (
      updatedPreviewData.mealPlanDays[dayIndex][mealType].recipes[recipeIndex]
        .rejected === undefined
    ) {
      updatedPreviewData.mealPlanDays[dayIndex][mealType].recipes[
        recipeIndex
      ].rejected = false;
    }

    // Toggle the rejected flag
    updatedPreviewData.mealPlanDays[dayIndex][mealType].recipes[
      recipeIndex
    ].rejected =
      !updatedPreviewData.mealPlanDays[dayIndex][mealType].recipes[recipeIndex]
        .rejected;

    setPreviewData(updatedPreviewData);
  };

  const hasRejectedRecipes = () => {
    if (!previewData) return false;

    for (const day of previewData.mealPlanDays) {
      for (const mealType of [
        "breakfast",
        "lunch",
        "dinner",
        "snack",
      ] as const) {
        for (const recipe of day[mealType].recipes) {
          if (recipe.rejected) return true;
        }
      }
    }

    return false;
  };

  const handleBackToForm = () => {
    setActiveView(null);
  };

  return (
    <NavHeader>
      <Container>
        {/* Custom toast component */}
        {tokenToast.visible && (
          <div
            className="fixed top-4 right-4 z-50 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg flex items-center"
            style={{ maxWidth: "90vw" }}
          >
            <span className="mr-2">🧠</span>
            <span>{tokenToast.message}</span>
            <button
              className="ml-4 text-white hover:text-gray-300"
              onClick={() =>
                setTokenToast((prev) => ({ ...prev, visible: false }))
              }
            >
              ✕
            </button>
          </div>
        )}

        <h2 className="font-bold inter-bold text-4xl mb-1">
          Meal Planning Tool
        </h2>
        <hr className="bg-passio-gray1 mb-8"></hr>

        {!apiKey ? (
          <Card>
            <div className="p-6 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                API Key Required
              </h3>
              <p className="text-gray-600 mb-4">
                An API key is required to use the meal planning tool. Please
                navigate to the Passio Hub Product Details page to get your API
                key.
              </p>
              <Button
                text="Go to Product Details"
                onClick={() =>
                  (window.location.href = "/details?product=passio-hub")
                }
              />
            </div>
          </Card>
        ) : error ? (
          <Card>
            <div className="p-6 text-center">
              <h3 className="text-lg font-medium text-red-600 mb-4">Error</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button text="Try Again" onClick={() => setError(null)} />
            </div>
          </Card>
        ) : (
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left side - Form */}
            <div className="w-full md:w-1/2">
              <Card>
                <MealPlanForm
                  onGenerateMealPlan={handleGenerateMealPlan}
                  onGeneratePreview={handleGeneratePreview}
                  isLoading={isLoading}
                />
              </Card>
            </div>

            {/* Right side - Results */}
            <div className="w-full md:w-1/2">
              <Card className="h-full">
                {activeView === "preview" && previewData ? (
                  <MealPlanPreview
                    mealPlanData={previewData}
                    onRejectRecipe={handleRejectRecipe}
                    onGenerateFull={() =>
                      handleGenerateMealPlan(
                        currentFormState || ({} as FormState)
                      )
                    }
                    onBack={handleBackToForm}
                    disableGenerate={hasRejectedRecipes()}
                    isLoading={isLoading}
                    onImportMealPlan={handleImportMealPlan}
                  />
                ) : activeView === "full" && fullMealPlan ? (
                  <MealPlanPreview
                    mealPlanData={fullMealPlan}
                    onRejectRecipe={() => {}}
                    onBack={handleBackToForm}
                    showFullDetails={true}
                    isLoading={isLoading}
                    onImportMealPlan={handleImportMealPlan}
                    apiClient={apiClient}
                  />
                ) : (
                  <MealPlanResults
                    isLoading={isLoading}
                    onImportMealPlan={handleImportMealPlan}
                  />
                )}
              </Card>
            </div>
          </div>
        )}
      </Container>
    </NavHeader>
  );
};

export default MealPlans;
